/**
 * LotsDrawer — the per-reagent lot list, FEFO-ordered (earliest expiry first) with the near-expiry risk
 * tier chip (critique/alerte/ok) and the days-remaining. Each lot row offers quarantine and a lot recall
 * launch. Drives 4 of the 5 states inline (offline/loading/error/empty); ForbiddenState is route-level.
 *
 * Data source: GET /inventory/reactifs/:id/risk (FEFO over all non-terminal lots of the reagent).
 */
import { useState } from "react";
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { INV_CAPS } from "../caps";
import { useLabId, useRisk } from "../api";
import type { Reactif, RiskLot } from "../types";
import { idStr } from "../types";
import { TierBadge } from "./StatusBadge";
import { LotStatutModal } from "./LotStatutModal";
import { LotMovementsDrawer } from "./LotMovementsDrawer";

export interface LotsDrawerProps {
  open: boolean;
  reactif: Reactif | null;
  onClose: () => void;
  /** Launch the recall worklist for a specific lot (handled by the parent screen). */
  onRecall: (lot: RiskLot) => void;
}

export function LotsDrawer({ open, reactif, onClose, onRecall }: LotsDrawerProps) {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canSet = useCan(INV_CAPS.mouvementCreate);
  const canRecall = useCan(INV_CAPS.recallRun);
  const reactifId = reactif ? idStr(reactif.id) : null;

  const riskQuery = useRisk(labId, reactifId, open && online && !!reactifId);
  const lots = riskQuery.data ?? [];

  const [quarantineLot, setQuarantineLot] = useState<RiskLot | null>(null);
  const [movementsLot, setMovementsLot] = useState<RiskLot | null>(null); // NOVA-058

  const columns: ReadonlyArray<Column<RiskLot>> = [
    {
      key: "numero",
      header: t("inventory.lots.col.numero"),
      mono: true,
      cell: (l) => l.numero_lot,
    },
    {
      key: "days",
      header: t("inventory.lots.col.days"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (l) => l.days_remaining,
      cell: (l) =>
        l.days_remaining < 0 ? (
          <span style={{ color: "var(--nv-danger)" }}>{t("inventory.lots.days.expired")}</span>
        ) : (
          <span className="nv-ltr-island">{l.days_remaining}</span>
        ),
    },
    {
      key: "tier",
      header: t("inventory.lots.col.tier"),
      cell: (l) => <TierBadge tier={l.tier} />,
    },
    {
      key: "actions",
      header: t("inventory.lots.col.actions"),
      align: "end",
      cell: (l) => (
        <div style={{ display: "inline-flex", gap: "var(--nv-s-2)", justifyContent: "flex-end" }}>
          {/* NOVA-058 — read-only ledger history; available to anyone who can read lots (this drawer). */}
          <Button size="sm" variant="ghost" onClick={() => setMovementsLot(l)}>
            {t("inventory.lots.action.movements")}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!canSet}
            onClick={() => setQuarantineLot(l)}
          >
            {t("inventory.lots.action.quarantine")}
          </Button>
          <Button size="sm" variant="ghost" disabled={!canRecall} onClick={() => onRecall(l)}>
            {t("inventory.lots.action.recall")}
          </Button>
        </div>
      ),
    },
  ];

  const body = () => {
    if (!online) return <OfflineState onRetry={() => void riskQuery.refetch()} cached={lots.length > 0} />;
    if (riskQuery.isLoading) return <LoadingState variant="table" rows={4} />;
    if (riskQuery.isError)
      return (
        <ErrorState
          message={riskQuery.error.message}
          code={riskQuery.error.code}
          onRetry={() => void riskQuery.refetch()}
        />
      );
    if (lots.length === 0)
      return (
        <EmptyState
          glyph="▢"
          title={t("inventory.lots.empty.title")}
          description={t("inventory.lots.empty.desc")}
        />
      );
    return (
      <Table
        columns={columns}
        rows={lots}
        rowKey={(l) => idStr(l.lot_id)}
        caption={t("inventory.lots.title")}
        pageSize={10}
      />
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
          {t("inventory.lots.title")}
          {reactif && <Badge tone="info">{reactif.code}</Badge>}
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("inventory.lots.subtitle")}</p>
        {body()}
      </div>

      <LotStatutModal
        open={!!quarantineLot}
        lotId={quarantineLot ? idStr(quarantineLot.lot_id) : null}
        {...(quarantineLot ? { numeroLot: quarantineLot.numero_lot } : {})}
        target="quarantaine"
        reactifId={reactifId}
        onClose={() => setQuarantineLot(null)}
        onDone={() => {
          setQuarantineLot(null);
          void riskQuery.refetch();
        }}
      />

      {/* NOVA-058 — the per-lot movement-history panel (read-only ledger). */}
      <LotMovementsDrawer
        open={!!movementsLot}
        lotId={movementsLot ? idStr(movementsLot.lot_id) : null}
        {...(movementsLot ? { numeroLot: movementsLot.numero_lot } : {})}
        onClose={() => setMovementsLot(null)}
      />
    </Drawer>
  );
}
